<template>
  <div
    :class="{
      'article-card--full': isFull,
      'article-card--compact': !isFull,
    }"
    class="article-card">
    <router-link
      :key="article.slug"
      :to="{ name: 'blog__article', params: {
        articleSlug: article.slug,
        gameNamespace: GameModel.ID_TO_DATA[article.category.game_id].routeNamespace,
      } }"
      class="article-card__wrapper">
      <div class="article-card__head">
        <img
          :data-src="gameLogo"
          alt=""
          class="article-card__logo lazyload"
          width="32"
          height="32">

        <div class="article-card__img-wrapper">
          <responsive-image
            :image-data="img"
            lazyload
            fill />
        </div>
      </div>

      <div
        class="article-card__body"
        :class="{'article-card__body--compact': !isFull}">
        <p
          v-if="isFull"
          class="article-card__tag">
          {{ article.category.title }}
        </p>

        <h3 class="article-card__title">
          {{ article.title }}
        </h3>

        <template v-if="isFull">
          <p class="article-card__text">
            {{ article.description }}
          </p>

          <button class="article-card__button transition--color">
            Read More
          </button>
        </template>
      </div>
    </router-link>
  </div>
</template>

<script>
import GameModel from 'Models/GameModel';

import ResponsiveImage from 'Components/ui/Image/ResponsiveImage.vue';

export default {
  name: 'ArticleCard',

  components: {
    ResponsiveImage,
  },

  props: {
    /*
      Объект статьи, принимает такие параметры
      {
        link: String,
        title: String,
        description: String,
        tag: String,
        game: String/Number,
        images: Object,
      }
     */
    article: {
      type: Object,
      required: true,
      default: () => ({}),
    },

    /* Вид карточки, с подробным описанием или только с заголовком и картинкой */
    isFull: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      GameModel,
    };
  },

  computed: {
    img() {
      if (!this.article?.images) {
        return {
          default: '/images/temp/card-bg.jpg',
        };
      }
      return {
        default: `/storage${this.article.images['450x350']}`,
        alt: this.article.image_alt || '',
        title: this.article.image_title || '',
        sources: [
          {
            media: '(-webkit-min-device-pixel-ratio: 1)',
            srcset: `/storage${this.article.images['450x350']} 1x`,
          },
          {
            media: '(-webkit-min-device-pixel-ratio: 2)',
            srcset: `/storage${this.article.images['900x700']} 2x`,
          },
        ],
      };
    },

    gameLogo() {
      return this.article.category.game_id
        ? GameModel.getGameLogo(this.article.category.game_id)
        : '';
    },
  },
};
</script>

<style lang="scss" scoped>
  .article-card {
    position: relative;
    overflow: hidden;
    height: 100%;
  }

  .article-card--full {
    --article-logo-size: 40px;
    --article-body-space: -100px;
    --article-title-size: #{$text-15px};

    .article-card__img-wrapper {
      @include ratio(272, 222);
    }

    .article-card__img-wrapper {
      -webkit-mask-image: -webkit-linear-gradient(90deg, rgba(white, 0) 0%, rgba(white, 1) 40%);
      mask-image: linear-gradient(0deg, rgba(white, 0) 0%, rgba(white, 1) 40%);
    }
  }

  .article-card--compact {
    --article-logo-size: 24px;
    --article-body-space: -12px;
    --article-title-size: #{$text-13px};

    .article-card__img-wrapper {
      @include ratio(188, 112);
    }

    .article-card__img-wrapper {
      -webkit-mask-image: -webkit-linear-gradient(90deg, rgba(white, 0) 10%, rgba(white, 1) 40%);
      mask-image: linear-gradient(0deg, rgba(white, 0) 10%, rgba(white, 1) 40%);
    }
  }

  .article-card__wrapper {
    position: relative;
    border-radius: 15px;
    overflow: hidden;
    width: 100%;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    color: $color--basic-white;
    background-color: $color--basic-black;
    border: 1px solid $color--basic-menu;
    transition: border-color var(--transition-color-time) var(--transition-color-easing);
  }

  .article-card__logo {
    width: var(--article-logo-size);
    height: var(--article-logo-size);
    object-fit: contain;
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
  }

  .article-card__img-wrapper {
    position: relative;
    display: block;
    overflow: hidden;
  }

  .article-card__body {
    padding: 0 24px 30px;
    margin-top: var(--article-body-space);
    position: relative;
    z-index: 1;
    text-align: center;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;

    > :not(:first-child) {
      margin-top: 16px;
    }
  }

  .article-card__body--compact {
    margin-top: 0;
  }

  .article-card__tag {
    padding: 0.6em 1.2em;
    font-size: $text-13px;
    font-weight: 500;
    line-height: 1.2;
    border-radius: 50px;
    background-color: $color--basic-blue;
  }

  .article-card__title {
    font-size: var(--article-title-size);
    font-weight: 500;
    line-height: 1.2;
  }

  .article-card__text {
    font-size: $text-13px;
    font-weight: 300;
    line-height: 1.2;
    color: $color--basic-light-gray;
  }

  .article-card__button {
    color: inherit;
    font-family: inherit;
    text-transform: uppercase;
    background: none;
    border: none;
    letter-spacing: 0.1em;
    font-size: $text-13px;
    font-weight: 500;
    line-height: 1.2;
  }

  @media screen and #{$screen-less-than-1024} {
    .article-card__button {
      display: none;
    }
  }

  @media screen and #{$screen-more-or-1024} {
    .article-card--full {
      --article-logo-size: 48px;
      --article-body-space: -32px;

      .article-card__img-wrapper {
        @include ratio(293, 216);
      }

      .article-card__logo {
        top: 20px;
        right: 20px;
      }
    }

  }

  @media #{$device--mouse} {
    .article-card:hover {
      .article-card__wrapper {
        border-color: $color--basic-dark-gray;
      }

      .article-card__button {
        color: $color--basic-blue;
      }
    }
  }
</style>
